import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, IconButton, Paper, List, ListItem, ListItemText, Popper, ClickAwayListener, Modal, Typography, CircularProgress, Chip, Button, Badge } from '@mui/material';
import { Image as ImageIcon, Mic as MicIcon, AttachFile as AttachFileIcon, InsertEmoticon as InsertEmoticonIcon, Send as SendIcon, Close as CloseIcon, ZoomOutMap as ZoomOutMapIcon, AutoAwesome as AutoAwesomeIcon, CloudDownload as CloudDownloadIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { AppDispatch } from '../../../../store';
import { useSendMessage } from 'lib/redux/linkedin/conversations/message-hook';
import { processSnippetText } from 'lib/redux/linkedin/snippets/slice';
import { getUrlSalesNavigator, renderSimplifiedConversationName } from 'lib/redux/linkedin/helpers';
import { SOURCE_TYPE } from 'lib/redux/linkedin/types';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { Expand, Shrink } from 'lucide-react';
import { useLinkedInNavigate } from '../../../../lib/redux/linkedin/navigate/hook';
import { getServicesManager } from 'services';
import { LinkedInGenRequest } from "interfaces/ai-automations";
import { ReduxSafeSalesConversation } from 'lib/redux/linkedin/conversations/sales-nav-slice';
import { ReduxSafeConversation } from 'lib/redux/linkedin/conversations/chat-slice';
import { useNavigate } from 'react-router-dom';
import { SettingsPages } from 'components/Settings';
import { VoiceMemoRecorder } from './VoiceMemoRecorder';
import { AttachmentMode, LinkedInOutgoingFile, LinkedInOutgoingVoiceMemo } from 'lib/linkedin';
import { VisualAudioPlayer } from 'components/VisualAudio/Player';
import { VoiceMemoList } from './VoiceMemoList';
import { LinkedInVoiceMemoMetadata } from 'interfaces/db';
import { loadVoiceMemoBlob } from 'lib/redux/linkedin/voice-memos/slice';

type PendingVoiceMemo = {
  blob: Blob
  duration: number
}

interface RecentVoiceMemo {
  id: string;
  name: string;
  blob?: Blob;
  duration: number;
  url: string;
}

interface LinkedInVoiceMemo {
  linkedin_voice_memo_id: string;
  name: string;
  duration: number;
  url: string;
}

export const MessageComposer: React.FC = React.memo(() => {
  const [message, setMessage] = useState('');
  const [showSnippets, setShowSnippets] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [snippetSearch, setSnippetSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const textFieldRef = useRef<HTMLDivElement>(null);
  const { canSend, sendMessage } = useSendMessage();
  const selectedConversation = useSelector((state: RootState) => state.linkedinSelected.conversationId);
  const snippets = useSelector((state: RootState) => state.linkedinSnippets.snippets);
  const emailValueProps = useSelector((state: RootState) => state.emailValueProps.value);
  const conversation = useSelector((state: RootState) => 
    selectedConversation ?
    selectedConversation?.sourceType === SOURCE_TYPE.CHAT 
      ? state.linkedinChatConversations.conversations[selectedConversation?.id]?.conversation
      : state.linkedinSalesNavigatorConversations.conversations[selectedConversation?.id]?.conversation
    : null
  );
  const navigate = useNavigate();
  const [attachedImages, setAttachedImages] = useState<Array<{ file: File; previewUrl: string }>>([]);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [attachedVoiceMemos, setAttachedVoiceMemos] = useState<PendingVoiceMemo[]>([]);
  const [showVoiceMemoAttachedBanner, setShowVoiceMemoAttachedBanner] = useState<boolean>(true);
  const [showImagePreview, setShowImagePreview] = useState<string | null>(null);
  const [showVoiceMemoRecordingUI, setShowVoiceMemoRecordingUI] = useState<boolean>(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const expandMode = useSelector((state: RootState) => state.linkedinSelected.expandMode);
  const { toExpandMode } = useLinkedInNavigate();
  const [recentVoiceMemos, setRecentVoiceMemos] = useState<RecentVoiceMemo[]>([]);
  const [loadingVoiceMemos, setLoadingVoiceMemos] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const voiceNotesInRedux = useSelector((state: RootState) => state.linkedinVoiceNotes.vms || []);
  const [loadingVoiceMemoId, setLoadingVoiceMemoId] = useState<string | null>(null);

  const handleSend = async () => {
    const trimmedMessage = message.trim();
    
    // Capture current state that we need to send
    const currentVoiceMemos = [...attachedVoiceMemos];
    const currentImages = [...attachedImages];
    const currentFiles = [...attachedFiles];
    const messageToSend = trimmedMessage;

    // Clear state immediately
    setMessage('');
    setAttachedVoiceMemos([]);
    setAttachedFiles([]);
    // Clean up all image preview URLs before clearing
    attachedImages.forEach(img => URL.revokeObjectURL(img.previewUrl));
    setAttachedImages([]);
    
    // If we have a voice memo, send it as a separate message first
    if (currentVoiceMemos.length > 0) {
      try {
        await sendMessage('', [{
          mode: AttachmentMode.VOICE_MEMO,
          blob: currentVoiceMemos[0].blob,
          duration: currentVoiceMemos[0].duration,
        }]);
      } catch (error) {
        console.error('Failed to send voice memo:', error);
        return;
      }
    }

    // Then send the text message with other attachments if any
    if (messageToSend || currentFiles.length > 0 || currentImages.length > 0) {
      const fileAttachments = [
        ...currentImages.map((img): LinkedInOutgoingFile => ({mode: AttachmentMode.FILE, blob: img.file})),
        ...currentFiles.map((file): LinkedInOutgoingFile => ({mode: AttachmentMode.FILE, blob: file})),
      ];
      
      try {
        await sendMessage(messageToSend, fileAttachments);
      } catch (error) {
        console.error('Failed to send message:', error);
      }
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (showSnippets) {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();
        
        const newIndex = event.key === 'ArrowUp'
          ? Math.max(0, selectedIndex - 1)
          : Math.min(filteredSnippets.length - 1, selectedIndex + 1);
        
        setSelectedIndex(newIndex);

        // Scroll selected item into view
        const listItem = document.querySelector(`[data-snippet-index="${newIndex}"]`);
        if (listItem) {
          listItem.scrollIntoView({ block: 'nearest' });
        }
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (filteredSnippets.length > 0) {
          handleSnippetSelect(filteredSnippets[selectedIndex].snippet_text);
        }
      } else if (event.key === 'Escape') {
        setShowSnippets(false);
      }
      return; // Stop here if snippets are shown
    }

    // Only handle message sending if no snippets are shown
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setMessage(newValue);

    // Check for snippet trigger
    const lastChar = newValue[newValue.length - 1];
    if (lastChar === '/') {
      setShowSnippets(true);
      setSnippetSearch('');
    } else if (showSnippets && !newValue.includes('/')) {
      setShowSnippets(false);
    } else if (showSnippets) {
      const searchText = newValue.split('/').pop() || '';
      setSnippetSearch(searchText.toLowerCase());
    }
  };

  const handleSnippetSelect = (snippetText: string) => {
    if (!conversation) return;
    
    if (!selectedConversation) return;
    
    const { name } = renderSimplifiedConversationName(selectedConversation, conversation);
    const firstName = name.split(' ')[0];
    
    const processedText = processSnippetText(snippetText, firstName);
    // Replace the entire text after the last slash with the processed snippet
    const lastSlashIndex = message.lastIndexOf('/');
    setMessage(lastSlashIndex >= 0 ? message.slice(0, lastSlashIndex) + processedText : processedText);
    setShowSnippets(false);
  };

  const filteredSnippets = snippets?.filter(snippet => 
    snippet.snippet_name.toLowerCase().includes(snippetSearch.toLowerCase())
  ) ?? [];

  // Also filter voice notes by the search term
  const filteredVoiceNotes = recentVoiceMemos.filter(memo => 
    memo.name.toLowerCase().includes(snippetSearch.toLowerCase())
  );

  // Reset selected index when filtered snippets change
  useEffect(() => {
    setSelectedIndex(0);
  }, [snippetSearch]);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const cursor = textFieldRef.current?.querySelector('textarea')?.selectionStart || message.length;
    const newMessage = message.slice(0, cursor) + emojiData.emoji + message.slice(cursor);
    setMessage(newMessage);
    setShowEmojiPicker(false);
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    const newImages = imageFiles.map(file => ({
      file,
      previewUrl: URL.createObjectURL(file)
    }));
    
    setAttachedImages(prev => [...prev, ...newImages]);
    
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter(file => {
      if (file.size > 25 * 1024 * 1024) {
        alert(`File ${file.name} is larger than 25MB`);
        return false;
      }
      return true;
    });
    
    setAttachedFiles(prev => [...prev, ...validFiles]);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    setAttachedImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].previewUrl);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles(prev => {
      const newFiles = [...prev];
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const handleAcceptVoiceMemoRecording = React.useCallback((blob: Blob, duration: number) => {
    setAttachedVoiceMemos([{blob, duration}]);
    setShowVoiceMemoRecordingUI(false);
    setShowVoiceMemoAttachedBanner(true);
  }, []);

  const handleCancelVoiceMemoRecording = React.useCallback(() => {
    setShowVoiceMemoRecordingUI(false);
  }, []);

  const handleRemoveVoiceMemo = (index: number) => {
    setAttachedVoiceMemos(prev => {
      const newVms = [...prev];
      newVms.splice(index, 1);
      return newVms;
    });
  };

  useEffect(() => {
    return () => {
      // Cleanup preview URLs when component unmounts
      attachedImages.forEach(img => URL.revokeObjectURL(img.previewUrl));
    };
  }, []);

  const handleImageButtonClick = () => {
    imageInputRef.current?.click();
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleGenerateAI = async () => {
    if (!conversation || !emailValueProps || emailValueProps.length === 0 || !selectedConversation) return;
    
    setIsGeneratingAI(true);
    try {
      const { name } = renderSimplifiedConversationName(selectedConversation, conversation);
      
      let messageHistory = null;
      let research = null;
      if (selectedConversation.sourceType === SOURCE_TYPE.CHAT) {
        const chatConversation = conversation as ReduxSafeConversation;
        messageHistory = chatConversation.messages ? JSON.stringify(chatConversation.messages.map((msg: any) => ({
          text: typeof msg.body === 'string' ? msg.body : msg.body?.text || '',
          timestamp: msg.deliveredAt,
          sender: msg.sender.entityUrn
        }))) : null;
        const otherParticipant = chatConversation.conversationParticipants.filter((v) => v.participantType?.member?.distance !== 'SELF')[0]
        if (otherParticipant) {
          research = `${name} - ${otherParticipant.participantType?.member?.headline}`
        } else {
          research = `${name} - No research`
        }
      } else {
        const salesConversation = conversation as ReduxSafeSalesConversation;
        messageHistory = salesConversation.messages ? JSON.stringify(salesConversation.messages.map((msg: any) => ({
          text: msg.body,
          timestamp: msg.deliveredAt,
          sender: msg.author
        }))) : null;
        research = `${name} - No research`
      }

      const request: LinkedInGenRequest = {
        prospect_name: name,
        platform_person_url: getUrlSalesNavigator(selectedConversation) ?? 'NO_URL_FOUND',
        value_prop: emailValueProps[0],
        research: research,
        message_history: messageHistory
      };

      const response = await getServicesManager().generateLinkedInMessage(request);
      if (response?.gen_message) {
        setMessage(response.gen_message);
      }
    } catch (error) {
      console.error('Failed to generate AI message:', error);
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // Load recent voice memos on mount
  useEffect(() => {
    const loadRecentMemos = async () => {
      setLoadingVoiceMemos(true);
      try {
        const memos = await getServicesManager().getLinkedInVoiceMemos();
        if (memos) {
          // Get current user to filter by user_id
          const currentUser = await getServicesManager().getUser();
          
          // Filter voice memos to only show those belonging to the current user
          const filteredMemos = currentUser 
            ? memos.filter((memo) => memo.user_id === currentUser.user_id)
            : memos;
            
          setRecentVoiceMemos(filteredMemos.slice(-5).map((memo: LinkedInVoiceMemoMetadata) => ({
            id: memo.linkedin_voice_memo_id,
            name: memo.name,
            duration: memo.duration || 0,
            url: '' // URL will be loaded when needed
          })));
        }
      } catch (error) {
        console.error('Failed to load recent voice memos:', error);
      } finally {
        setLoadingVoiceMemos(false);
      }
    };
    loadRecentMemos();
  }, []);

  const handleSelectVoiceMemo = async (memo: RecentVoiceMemo) => {
    try {
      // First check if we already have the blob
      if (memo.blob) {
        setAttachedVoiceMemos([{ blob: memo.blob, duration: memo.duration }]);
        setShowVoiceMemoAttachedBanner(true);
        setShowSnippets(false); // Close the snippets modal
        setMessage(''); // Clear the message
        return;
      }
      
      // Check if it's already loaded in Redux store
      const reduxMemo = voiceNotesInRedux.find(vm => vm.linkedin_voice_memo_id === memo.id);
      
      if (reduxMemo?.blob) {
        // If we have it in Redux, use it directly
        setAttachedVoiceMemos([{ blob: reduxMemo.blob, duration: memo.duration }]);
        setShowVoiceMemoAttachedBanner(true);
        setShowSnippets(false); // Close the snippets modal
        setMessage(''); // Clear the message
        return;
      }
      
      // Otherwise, load it using the Redux thunk
      const loadingToast = document.createElement('div');
      loadingToast.textContent = 'Loading voice note...';
      loadingToast.style.position = 'fixed';
      loadingToast.style.bottom = '20px';
      loadingToast.style.left = '50%';
      loadingToast.style.transform = 'translateX(-50%)';
      loadingToast.style.padding = '10px 20px';
      loadingToast.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
      loadingToast.style.color = 'white';
      loadingToast.style.borderRadius = '4px';
      loadingToast.style.zIndex = '9999';
      document.body.appendChild(loadingToast);
      
      // Set loading state for this specific memo
      setLoadingVoiceMemoId(memo.id);
      
      try {
        const result = await dispatch(loadVoiceMemoBlob(memo.id)).unwrap();
        
        if (result.blob) {
          setAttachedVoiceMemos([{ blob: result.blob, duration: memo.duration }]);
          setShowVoiceMemoAttachedBanner(true);
          setShowSnippets(false); // Close the snippets modal
          setMessage(''); // Clear the message
          // Show success toast
          loadingToast.textContent = 'Voice note added!';
          loadingToast.style.backgroundColor = 'rgba(22, 163, 74, 0.9)';
          setTimeout(() => {
            document.body.removeChild(loadingToast);
          }, 1500);
        } else {
          throw new Error('Failed to load voice note');
        }
      } catch (error) {
        loadingToast.textContent = 'Failed to load voice note';
        loadingToast.style.backgroundColor = 'rgba(220, 38, 38, 0.9)';
        setTimeout(() => {
          document.body.removeChild(loadingToast);
        }, 1500);
        throw error;
      } finally {
        // Clear loading state
        setLoadingVoiceMemoId(null);
      }
    } catch (error) {
      console.error('Failed to load voice memo:', error);
      // Ensure loading state is cleared even on error
      setLoadingVoiceMemoId(null);
    }
  };

  return (
    <Box sx={{ 
      p: 2,
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
      borderTop: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'background.paper',
      position: 'relative'
    }}>
      {/* Hidden file inputs */}
      <input
        type="file"
        ref={imageInputRef}
        onChange={handleImageSelect}
        accept="image/*"
        multiple
        style={{ display: 'none' }}
      />
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        multiple
        style={{ display: 'none' }}
      />

      {/* Attached files preview */}
      {(attachedImages.length > 0 || attachedVoiceMemos.length > 0 || attachedFiles.length > 0) && (
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          mb: 1
        }}>
          {(attachedImages.length > 0 || attachedVoiceMemos.length > 0) && (
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: 1
            }}>
              {attachedImages.map((img, index) => (
                <Box
                  key={`img-${index}`}
                  sx={{ 
                    p: 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Box 
                    component="img"
                    src={img.previewUrl}
                    alt={`Preview ${index + 1}`}
                    sx={{ 
                      width: 40,
                      height: 40,
                      objectFit: 'cover',
                      borderRadius: 0.5,
                      cursor: 'pointer'
                    }}
                    onClick={() => setShowImagePreview(img.previewUrl)}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap variant="body2">
                      {img.file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(img.file.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveImage(index)}
                      sx={{ color: 'text.secondary' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setShowImagePreview(img.previewUrl)}
                      sx={{ color: 'text.secondary' }}
                    >
                      <ZoomOutMapIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
              {attachedVoiceMemos.map((vm, index) => (
                <Box
                  key={`vm-${index}`}
                  sx={{
                    p: 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <VisualAudioPlayer
                    file={vm.blob}
                    initialDuration={vm.duration} />
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveVoiceMemo(index)}
                      sx={{ color: 'text.secondary' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {attachedFiles.length > 0 && (
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: 1
            }}>
              {attachedFiles.map((file, index) => (
                <Box
                  key={index}
                  sx={{ 
                    p: 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Box 
                    sx={{ 
                      width: 40,
                      height: 40,
                      backgroundColor: 'white',
                      borderRadius: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <AttachFileIcon sx={{ color: 'text.secondary' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap variant="body2">
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFile(index)}
                    sx={{ color: 'text.secondary' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5
      }}>
        {/* Display attached voice memo if any */}
        {attachedVoiceMemos.length > 0 && showVoiceMemoAttachedBanner && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              borderRadius: '8px',
              bgcolor: 'rgba(10, 102, 194, 0.08)',
              border: '1px solid rgba(10, 102, 194, 0.2)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  bgcolor: '#0a66c2',
                  color: 'white'
                }}
              >
                <MicIcon fontSize="small" />
              </Box>
              <Typography sx={{ fontWeight: 500 }}>
                Voice note attached
              </Typography>
            </Box>
            <IconButton
              onClick={() => setShowVoiceMemoAttachedBanner(false)}
              size="small"
              sx={{
                color: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.08)',
                  color: 'rgba(0, 0, 0, 0.7)'
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        <TextField
          fullWidth
          multiline
          maxRows={6}
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
          placeholder="Write a message... (Type / for snippets and voice notes)"
          variant="outlined"
          size="small"
          ref={textFieldRef}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#f3f3f3',
              minHeight: '44px',
              borderRadius: '12px',
              '& fieldset': {
                border: 'none'
              },
              '&:hover fieldset': {
                border: 'none'
              },
              '&.Mui-focused fieldset': {
                border: 'none'
              },
              '& textarea': {
                p: 1.5
              }
            }
          }}
        />
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Attachment buttons.
                LinkedIn mobile has broken rendering if voice memos and other attachments come in the same message
                (as of 2025-04-02) so don't let the user create such messages. */}
            {attachedVoiceMemos.length === 0 && <IconButton
              title='Attach pictures'
              size="small" 
              onClick={handleImageButtonClick}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <Badge color="primary" badgeContent={attachedImages.length}>
                <ImageIcon sx={{ fontSize: 20 }} />
              </Badge>
            </IconButton>}
            {(!selectedConversation || selectedConversation.sourceType === SOURCE_TYPE.CHAT)
            && attachedFiles.length === 0 && attachedImages.length === 0
            && <IconButton
              title={attachedVoiceMemos.length ? 'Replace voice memo' : 'Record voice memo'}
              size="small"
              disabled={showVoiceMemoRecordingUI}
              onClick={() => setShowVoiceMemoRecordingUI(true)}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: 'action.hover',
                }
              }}
            > {/* see note in handleAcceptVoiceMemoRecording - can only send one voice memo at a time so don't show a number */}
              <Badge color="primary" variant="dot" invisible={!attachedVoiceMemos.length}>
                <MicIcon sx={{ fontSize: 20 }} />
              </Badge>
            </IconButton>}
            {attachedVoiceMemos.length === 0 && <IconButton
              title='Attach files'
              size="small"
              onClick={handleFileButtonClick}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <Badge color="primary" badgeContent={attachedFiles.length}>
                <AttachFileIcon sx={{ fontSize: 20 }} />
              </Badge>
            </IconButton>}
            <IconButton 
              title='Emoji'
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                setShowEmojiPicker(!showEmojiPicker);
              }}
              sx={{
                backgroundColor: showEmojiPicker ? 'action.selected' : 'transparent',
                '&:hover': {
                  backgroundColor: showEmojiPicker ? 'action.selected' : 'action.hover'
                }
              }}
            >
              <InsertEmoticonIcon sx={{ fontSize: 20 }} />
            </IconButton>
            {emailValueProps && emailValueProps.length > 0 && (
              <IconButton
                title='Ask AI for a suggestion'
                size="small"
                onClick={handleGenerateAI}
                disabled={isGeneratingAI}
                sx={{
                  color: isGeneratingAI ? 'primary.main' : 'text.secondary',
                  position: 'relative',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 20 }} />
                {isGeneratingAI && (
                  <CircularProgress
                    size={28}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-14px',
                      marginLeft: '-14px'
                    }}
                  />
                )}
              </IconButton>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton 
              onClick={() => toExpandMode(!expandMode)} 
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              {expandMode ? <Shrink style={{ width: 20, height: 20 }} /> : <Expand style={{ width: 20, height: 20 }} />}
            </IconButton>
            <IconButton 
              onClick={handleSend}
              disabled={(!message.trim() && attachedImages.length === 0 && attachedFiles.length === 0 && attachedVoiceMemos.length === 0) || !canSend}
              size="small"
              sx={{ 
                backgroundColor: (!message.trim() && attachedImages.length === 0 && attachedFiles.length === 0) ? undefined : '#0a66c2',
                color: (!message.trim() && attachedImages.length === 0 && attachedFiles.length === 0) ? undefined : 'white',
                '&:hover': {
                  backgroundColor: (!message.trim() && attachedImages.length === 0 && attachedFiles.length === 0) ? undefined : '#004182'
                }
              }}
            >
              <SendIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Modal
        open={!!showImagePreview}
        onClose={() => setShowImagePreview(null)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Box
          sx={{
            position: 'relative',
            maxWidth: '90vw',
            maxHeight: '90vh',
            outline: 'none',
          }}
        >
          <IconButton
            onClick={() => setShowImagePreview(null)}
            sx={{
              position: 'absolute',
              top: -40,
              right: 0,
              color: 'white'
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={showImagePreview || ''}
            alt="Preview"
            sx={{
              maxWidth: '100%',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 1,
              boxShadow: 24
            }}
          />
        </Box>
      </Modal>

      {showVoiceMemoRecordingUI && (
        <Box 
          onClick={(e) => {
            e.stopPropagation();
            handleCancelVoiceMemoRecording();
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
        >
          <Box onClick={(e) => e.stopPropagation()}>
            <VoiceMemoRecorder
              onAccept={handleAcceptVoiceMemoRecording}
              onCancel={handleCancelVoiceMemoRecording}
              initialBlob={attachedVoiceMemos[0]?.blob}
              recentVoiceMemos={recentVoiceMemos}
            />
          </Box>
        </Box>
      )}

      {showEmojiPicker && (
        <Popper
          open={true}
          anchorEl={textFieldRef.current}
          placement="top-start"
          style={{ zIndex: 1300 }}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ]}
        >
          <ClickAwayListener onClickAway={() => setShowEmojiPicker(false)}>
            <Box sx={{ 
              backgroundColor: 'background.paper',
              borderRadius: 1,
              boxShadow: 3,
              '.EmojiPickerReact': {
                '--epr-bg-color': 'transparent',
                '--epr-category-label-bg-color': 'transparent',
                border: 'none',
                boxShadow: 'none'
              }
            }}>
              <EmojiPicker 
                onEmojiClick={(emojiData) => {
                  handleEmojiClick(emojiData);
                  setShowEmojiPicker(false);
                }}
                autoFocusSearch={false}
                width={320}
                height={400}
              />
            </Box>
          </ClickAwayListener>
        </Popper>
      )}

      {showSnippets && textFieldRef.current && (
        <Popper
          open={true}
          anchorEl={textFieldRef.current}
          placement="top-start"
          style={{ width: textFieldRef.current.clientWidth, zIndex: 1300 }}
        >
          <ClickAwayListener onClickAway={() => setShowSnippets(false)}>
            <Paper 
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
                mt: 1
              }}
            >
              {/* Snippets Section */}
              <Box>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 0.75,
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      color: 'text.secondary',
                    }}
                  >
                    Snippets
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => navigate(`/settings?tab=${SettingsPages.LINKEDIN}`)}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      minWidth: 0,
                      padding: '1px 6px',
                      color: 'primary.main',
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(10, 102, 194, 0.04)'
                      }
                    }}
                  >
                    Configure
                  </Button>
                </Box>
                <List sx={{ 
                  maxHeight: 210,
                  overflow: 'auto',
                  py: 1,
                  px: 0.5
                }}>
                  {filteredSnippets.length > 0 ? (
                    filteredSnippets.map((snippet, index) => (
                      <ListItem
                        key={snippet.snippet_id}
                        component="div"
                        onClick={() => handleSnippetSelect(snippet.snippet_text)}
                        data-snippet-index={index}
                        sx={{
                          py: 0.75,
                          px: 1.5,
                          cursor: 'pointer',
                          borderRadius: 1,
                          backgroundColor: index === selectedIndex ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        <ListItemText 
                          primary={
                            <Typography sx={{ 
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              color: 'text.primary',
                              mb: 0.5
                            }}>
                              {snippet.snippet_name}
                            </Typography>
                          }
                          secondary={
                            <Typography 
                              component="div" 
                              sx={{ 
                                color: 'text.secondary',
                                fontSize: '0.8125rem',
                                lineHeight: 1.5
                              }}
                            >
                              {snippet.snippet_text.split(/(\{\{name\}\})/).map((part: string, i: number) => (
                                part === '{{name}}' ? (
                                  <Box
                                    key={i}
                                    component="span"
                                    sx={{
                                      mx: 0.5,
                                      px: 0.75,
                                      py: 0.25,
                                      borderRadius: 0.75,
                                      bgcolor: 'rgba(0, 0, 0, 0.04)',
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                      color: 'text.primary',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    name
                                  </Box>
                                ) : (
                                  <span key={i}>{part}</span>
                                )
                              ))}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem sx={{ py: 2, px: 2 }}>
                      <ListItemText 
                        primary={
                          <Typography sx={{ 
                            fontSize: '0.875rem',
                            color: 'text.secondary',
                            textAlign: 'center'
                          }}>
                            No snippets found
                          </Typography>
                        }
                      />
                    </ListItem>
                  )}
                </List>
              </Box>

              {/* VOICE NOTES Section - Only show for non-Sales Navigator conversations */}
              {selectedConversation?.sourceType === SOURCE_TYPE.CHAT && (
                <Box sx={{ mt: 1 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      py: 0.75, 
                      px: 2, 
                      fontSize: '0.75rem',
                      borderTop: '1px solid',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'rgba(0, 0, 0, 0.02)'
                    }}
                  >
                    <Typography sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                      Voice Notes
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => navigate(`/settings?tab=${SettingsPages.LINKEDIN}`)}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        minWidth: 0,
                        padding: '1px 6px',
                        color: 'primary.main',
                        borderRadius: 1,
                        '&:hover': {
                          backgroundColor: 'rgba(10, 102, 194, 0.04)'
                        }
                      }}
                    >
                      Configure
                    </Button>
                  </Box>

                  {loadingVoiceMemos ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
                      <CircularProgress size={16} thickness={4} sx={{ color: 'text.secondary' }} />
                    </Box>
                  ) : filteredVoiceNotes && filteredVoiceNotes.length > 0 ? (
                    <Box sx={{ maxHeight: 210, overflow: 'auto', pt: 0.5, pb: 0.5 }}>
                      <VoiceMemoList
                        voiceMemos={filteredVoiceNotes}
                        onSelect={handleSelectVoiceMemo}
                        loadingMemoId={loadingVoiceMemoId}
                      />
                    </Box>
                  ) : null}
                </Box>
              )}

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'rgba(0, 0, 0, 0.01)'
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                  }}
                >
                  Type to search snippets and voice notes
                </Typography>
              </Box>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </Box>
  );
}); 